import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Dispatch, MutableRefObject, SetStateAction, useContext, useEffect, useState } from "react";
import { SuccessAlertContext } from "App";
import { Supplier } from "features/order/types";
import CustomTextField from "./CustomTextField";
import productSelectApi from "../api/productSelectApi";
import SurroundLine from "./SurroundLine";
import { GenuineIncrementalSearchElement, IncrementalSearchElement } from "./Common";
import { ManagedItem, ItemSelectResponse, ItemSelectParams, ItemSearch } from "../types";
import { removeEmptyValues } from "../utils/common";

const incrementalSearchOptionLength = 10;
type Props = {
  items: ManagedItem[];
  selectedRows: ItemSearch[];
  open: boolean;
  isLoading: boolean;
  productLoading: boolean;
  isFastLoading?: MutableRefObject<boolean>;
  onClose: () => void;
  productTypeOptions: readonly ["" | "タイヤ", "ホイール"];
  productType: "" | "タイヤ" | "ホイール";
  productSrcOptions: Supplier[];
  productSrc: string;
  brandOptions: string[];
  companyCd: string;
  classCd: string;
  brand: string;
  className: string;
  widthOptions: number[];
  width?: number;
  oblatenessOptions: number[];
  oblateness?: number;
  tireRimDmtrOptions: number[];
  tireRimDmtr?: number;
  wheelRimDmtrOptions: number[];
  wheelRimDmtr?: number;
  rimWidthOptions: number[];
  rimWidth?: number;
  holeNmbrOptions: number[];
  holeNmbr?: number;
  PCDOptions: number[];
  PCD?: number;
  itemName: string;
  bckgrndClr: string;
  productItems: ItemSearch[];
  ActionButtons: JSX.Element;
  getMoreStockList?: Function;
  setSelectedRows: Dispatch<SetStateAction<ItemSearch[]>>;
  onSearch: () => Promise<void>;
  search: (params: ItemSelectParams) => Promise<void>;
  onApply: (rows: ManagedItem[]) => Promise<void>;
  setProductType: Dispatch<SetStateAction<"" | "タイヤ" | "ホイール">>;
  setProductSrc: Dispatch<SetStateAction<string>>;
  setBrand: Dispatch<SetStateAction<string>>;
  setWidth: Dispatch<SetStateAction<number | undefined>>;
  setOblateness: Dispatch<SetStateAction<number | undefined>>;
  setTireRimDmtr: Dispatch<SetStateAction<number | undefined>>;
  setWheelRimDmtr: Dispatch<SetStateAction<number | undefined>>;
  setRimWidth: Dispatch<SetStateAction<number | undefined>>;
  setHoleNmbr: Dispatch<SetStateAction<number | undefined>>;
  setPCD: Dispatch<SetStateAction<number | undefined>>;
  setItemName: Dispatch<SetStateAction<string>>;
  setProductItems: Dispatch<SetStateAction<ItemSearch[]>>;
};

const styles: { [key: string]: SxProps } = {
  searchRowBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
};

const commonTableCellStyles = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 500,
  fontSize: 11,
  letterSpacing: "0.01071em",
  display: "table-cell",
  verticalAlign: "inherit",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  textAlign: "left",
  padding: "16px",
  paddingTop: "20px",
  paddingBottom: "20px",
  color: "rgba(0, 0, 0, 0.87)",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  maxWidth: "110px",
  borderLeft: "1px solid rgb(224, 224, 224)",
  borderTop: "1px solid rgb(224, 224, 224)",
};

/**
 * Product Register modal 商品登録モーダル
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
export default function ProductCodeSearchDialog({
  items,
  selectedRows,
  open,
  isLoading,
  isFastLoading,
  productLoading,
  onClose,
  productTypeOptions,
  productType,
  productSrcOptions,
  productSrc,
  brandOptions,
  companyCd,
  classCd,
  brand,
  className,
  widthOptions,
  width,
  oblatenessOptions,
  oblateness,
  tireRimDmtrOptions,
  tireRimDmtr,
  wheelRimDmtrOptions,
  wheelRimDmtr,
  rimWidthOptions,
  rimWidth,
  holeNmbrOptions,
  holeNmbr,
  PCDOptions,
  PCD,
  itemName,
  bckgrndClr,
  ActionButtons,
  productItems,
  getMoreStockList,
  setSelectedRows,
  onSearch,
  search,
  onApply,
  setProductType,
  setProductSrc,
  setBrand,
  setWidth,
  setOblateness,
  setTireRimDmtr,
  setWheelRimDmtr,
  setRimWidth,
  setHoleNmbr,
  setPCD,
  setItemName,
  setProductItems,
}: Props) {
  let title = "";
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);

  // スクロール処理
  // const handleScroll = async () => {
  //   // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
  //   if (isFastLoading?.current || !contentRef.current) {
  //     return;
  //   }
  //   const scrollTop = contentRef.current?.scrollTop;
  //   const contentHeight = contentRef.current?.clientHeight;
  //   const scrollHeight = contentRef.current?.scrollHeight;
  //   if (scrollTop + contentHeight >= scrollHeight) {
  //     setFireMoreLoadEvent((prev) => !prev);
  //   }
  // };

  const { showAlert } = useContext(SuccessAlertContext);

  // 右側商品行列選択
  const isSelected = (row: ItemSearch) => selectedRows.some((r) => r.item_cd === row.item_cd);
  const rowToggle = (row: ItemSearch) => {
    setSelectedRows((prev) =>
      prev.some((r) => r.item_cd === row.item_cd)
        ? prev.filter((r) => r.item_cd !== row.item_cd)
        : [...prev, row],
    );
  };

  const getClassCd = (pType: "" | "タイヤ" | "ホイール") => {
    if (pType === "タイヤ") return "02"
    if (pType === "ホイール") return "03"
    return "";
  }

  // サイドボタン検索
  const handleSearchClick = async () => {
    setProductItems([]);

    // !検証
    const params: ItemSelectParams = {
      limit: 200,
      offset: 0,
      company_cd: companyCd,
      class_cd: getClassCd(productType),
      vendor_cd: productSrcOptions.find(po => productSrc === po.supplierName)?.supplierCd || undefined,
      brand: brand != null ? String(brand) : undefined,
      item_name: itemName != null ? String(itemName) : undefined,
      tire_width: productType === "タイヤ" ? width : undefined,
      tire_ratio: productType === "タイヤ" ? oblateness : undefined,
      tire_rim_size: productType === "タイヤ" ? tireRimDmtr : undefined,
      wheel_width: productType === "ホイール" ? rimWidth : undefined,
      wheel_rim_size: productType === "ホイール" ? wheelRimDmtr : undefined,
      wheel_hole: productType === "ホイール" ? holeNmbr : undefined,
      wheel_pcd: productType === "ホイール" ? PCD : undefined,
    };
    search(removeEmptyValues(params));
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // const handleClose = () => {
  //   onClose();
  // };

  const searchTableBodyElement = () => {
    if (productLoading) {
      return (
        <TableRow>
          <TableCell colSpan={3} align="center">
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (productItems.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} align="center">
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              検索商品はまだありません
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return productItems.map((item) => (
      <TableBody>
        <TableRow
          key={item.item_cd}
          role="checkbox"
          tabIndex={-1}
          selected={isSelected(item)}
          onClick={() => rowToggle(item)}
          hover
        >
          <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50, backgroundColor: bckgrndClr }}>
            {item.item_cd}
            &emsp;
            {item.item_name}{item.size}
          </TableCell>
        </TableRow>
      </TableBody>
    ));
  };

  const searchElementRow = () => {
    const searchElements = [
      // <IncrementalSearchElement
      //   key="productSrc"
      //   label="仕入先名"
      //   options={productSrcOptions}
      //   value={productSrc}
      //   onChange={(val: string) => setProductSrc(val)}
      //   optionLength={incrementalSearchOptionLength}
      // />,
      <GenuineIncrementalSearchElement
        key="productSrc"
        label="仕入先名"
        options={productSrcOptions.map((s) => s.supplierName)}
        value={productSrc}
        onChange={(val: string) => setProductSrc(val)}
        optionLength={incrementalSearchOptionLength}
        isBlankNoOptions
      />,
      // <IncrementalSearchElement
      //   key="brand"
      //   label="ブランド"
      //   options={brandOptions}
      //   value={brand}
      //   onChange={(val: string) => setBrand(val)}
      //   optionLength={incrementalSearchOptionLength}
      // />,
      <GenuineIncrementalSearchElement
        key="brand"
        label="ブランド"
        options={brandOptions}
        value={brand}
        onChange={setBrand}
        optionLength={incrementalSearchOptionLength}
        isBlankNoOptions
      />,
    ];

    let searchElementsPerType: JSX.Element[] = [];
    switch (productType) {
      case "":
        break;

      case "タイヤ":
        searchElementsPerType = [
          <IncrementalSearchElement
            key="width"
            label="幅"
            options={widthOptions.map((t) => `${t}`)}
            value={`${width ?? ""}`}
            onChange={(val: string) => {
              setWidth(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
          <IncrementalSearchElement
            key="oblateness"
            label="扁平率"
            options={oblatenessOptions.map((t) => `${t}`)}
            value={`${oblateness ?? ""}`}
            onChange={(val: string) => {
              setOblateness(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
          <IncrementalSearchElement
            key="tireRimDmtr"
            label="タイヤリム径"
            options={tireRimDmtrOptions.map((t) => `${t}`)}
            value={`${tireRimDmtr ?? ""}`}
            onChange={(val: string) => {
              setTireRimDmtr(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
        ];
        title = "タイヤサイズ";
        break;

      case "ホイール":
        searchElementsPerType = [
          <IncrementalSearchElement
            key="wheelRimDmtr"
            label="ホイールリム径"
            options={wheelRimDmtrOptions.map((t) => `${t}`)}
            value={`${wheelRimDmtr ?? ""}`}
            onChange={(val: string) => {
              setWheelRimDmtr(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
          <IncrementalSearchElement
            key="rimWidth"
            label="リム幅"
            options={rimWidthOptions.map((t) => `${t}`)}
            value={`${rimWidth ?? ""}`}
            onChange={(val: string) => {
              setRimWidth(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
          <IncrementalSearchElement
            key="holeNmbr"
            label="ホール数"
            options={holeNmbrOptions.map((t) => `${t}`)}
            value={`${holeNmbr ?? ""}`}
            onChange={(val: string) => {
              setHoleNmbr(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
          <IncrementalSearchElement
            key="PCD"
            label="PCD"
            options={PCDOptions.map((t) => `${t.toFixed(2)}`)}
            value={`${PCD ? PCD.toFixed(2) : ""}`}
            onChange={(val: string) => {
              setPCD(val === "" ? undefined : Number(val));
            }}
            optionLength={incrementalSearchOptionLength}
            isContainBlank
          />,
        ];
        title = "ホイールサイズ";
        break;

      default:
        break;
    }

    const len = Math.ceil(searchElements.length / 2);
    const couples: JSX.Element[] = [];
    for (let i = 0; i < len; i += 1) {
      const element1 = searchElements[i * 2];
      let element2: JSX.Element | undefined;
      if (i * 2 + 1 < searchElements.length) {
        element2 = searchElements[i * 2 + 1];
      }
      couples.push(
        <Box display="flex" width="100%">
          {element1}
          {element2 ?? <> </>}
        </Box>,
      );
    }

    const srchElmntsPerTypeLen = Math.ceil(searchElementsPerType.length / 2);
    const tirecouples: JSX.Element[] = [];
    for (let i = 0; i < srchElmntsPerTypeLen; i += 1) {
      const element1 = searchElementsPerType[i * 2];
      let element2: JSX.Element | undefined;
      if (i * 2 + 1 < searchElementsPerType.length) {
        element2 = searchElementsPerType[i * 2 + 1];
      }
      tirecouples.push(
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          {element1}
          {element2 ?? <> </>}
        </Box>,
      );
    }

    return (
      <Box minWidth="388px" width="100%">
        <Typography>1.設定する商品を検索してください。</Typography>
        <Box sx={{ width: "100%", justifyItems: "center", marginTop: 6 }}>
          <IncrementalSearchElement
            key="productType"
            label="タイヤ/ホイール"
            options={productTypeOptions}
            value={productType}
            onChange={(val: "" | "タイヤ" | "ホイール") => setProductType(val)}
            optionLength={incrementalSearchOptionLength}
            minWidth="222px"
            width="auto"
            required
          />
        </Box>
        <div>
          {couples}
          <Grid item sm={12} padding={1}>
            <Box key="product_cd" width="100%">
              <CustomTextField
                id="orderNumber"
                type="text"
                label="ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)"
                text={itemName}
                setText={setItemName}
                maxLength={100}
                size="small"
                fullWidth
              />
            </Box>
          </Grid>
        </div>
        {productType && (
          <SurroundLine title={title} sx={{ padding: 2, marginTop: 2 }}>
            <Box>
              {tirecouples}
            </Box>
          </SurroundLine>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <Box p={2}>
        <DialogTitle>得意先企業の商品設定</DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <DialogContent
            sx={{
              overflow: "visible",
              width: "auto",
              border: "solid 2px #d3d3d3",
              borderRadius: 2,
              padding: 2,
              margin: 2,
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <CircularProgress size={80} />
              </Box>
            )}
            {searchElementRow()}
            <DialogActions>
              <Button
                variant="contained"
                onClick={async () => {
                  if (open && productType === "") {
                    showAlert("タイヤ/ホイールを選択してください。", "error");
                    return;
                  }
                  handleSearchClick();
                }}
                disabled={productLoading}
              >
                {productLoading ? "検索中..." : "検索"}
              </Button>
            </DialogActions>
          </DialogContent>
          {/* 右側テーブル */}
          <DialogContent
            sx={{
              overflow: "auto",
              border: "solid 2px #d3d3d3",
              borderRadius: 2,
              padding: 2,
              margin: 2,
              height: "473px",
            }}
          >
            <Typography>2.選択して追加してください。</Typography>
            <Table sx={{ marginTop: 2 }}>{searchTableBodyElement()}</Table>
          </DialogContent>
        </Box>
        <DialogActions>{ActionButtons}</DialogActions>
      </Box>
    </Dialog>
  );
}
